import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { LOGIN, REGISTER } from "@/constants/testIds/auth";
import {
  forgotPasswordRequest,
  formatApiError,
  resetPasswordRequest,
  roleLabel,
} from "@/lib/api";
import { DEV_TOOLS_ENABLED } from "@/hooks/useAuth";
import { isValidPhone } from "@/lib/phone";

const DEV_ROLES = ["customer", "ambassador", "trade_partner", "admin"];

export const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir",
  "Ladakh", "Lakshadweep", "Puducherry",
];

// Applications carry an ISO timestamp; members read a plain Indian date.
const formatSubmittedOn = (value) => {
  const submitted = value ? new Date(value) : null;
  if (!submitted || Number.isNaN(submitted.getTime())) return null;
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(submitted);
};

const Field = ({ id, label, hint, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-[13px] font-medium text-brand-ink">
      {label}
    </Label>
    <Input
      id={id}
      className={`h-11 rounded-[12px] border-black/10 ${props.readOnly ? "bg-black/[.03] text-brand-muted" : "bg-white"}`}
      {...props}
    />
    {hint && <p className="text-[11px] text-brand-muted">{hint}</p>}
  </div>
);

const Shell = ({ title, subtitle, children, footer }) => (
  <div className="grid min-h-screen place-items-center bg-brand-canvas px-4 py-10 text-brand-ink">
    <div className="w-full max-w-[420px]">
      <div className="mb-7 flex items-center justify-center gap-3">
        <img src="/logo-mark.png" alt="Greentek Alliance" className="h-14 w-14" />
        <div className="text-left leading-[1.08]">
          <span className="block text-[22px] font-bold uppercase tracking-[0.08em] text-brand-ink">Greentek</span>
          <span className="block text-[22px] font-bold uppercase tracking-[0.08em] text-brand-gold">Alliance</span>
        </div>
      </div>
      <div className="rounded-[20px] border border-black/5 bg-white/80 p-7 shadow-xl backdrop-blur-xl">
        <h1 className="brand-title text-2xl font-semibold">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm text-brand-muted">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
      {footer && <div className="mt-5 text-center text-sm text-brand-muted">{footer}</div>}
    </div>
  </div>
);

const SubmitButton = ({ loading, children, ...props }) => (
  <Button
    type="submit"
    disabled={loading}
    className="h-11 w-full rounded-[12px] bg-brand-forest text-white hover:bg-[#0d322d]"
    {...props}
  >
    {loading && <Loader2 className="mr-2 animate-spin" size={16} />}
    {children}
  </Button>
);

const LinkButton = ({ onClick, children, ...props }) => (
  <button
    type="button"
    onClick={onClick}
    className="font-medium text-brand-gold-deep underline-offset-2 hover:underline"
    {...props}
  >
    {children}
  </button>
);

const GOOGLE_ENABLED = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

// "or" divider + Google's sign-in button (ID-token flow). Renders nothing when
// Google sign-in isn't configured. onGoogle receives the Google credential.
const GoogleAuthButton = ({ onGoogle }) => {
  if (!GOOGLE_ENABLED) return null;

  const handleSuccess = async (credentialResponse) => {
    const credential = credentialResponse?.credential;
    if (!credential) {
      toast.error("Could not sign in with Google");
      return;
    }
    try {
      await onGoogle(credential);
    } catch (error) {
      toast.error(formatApiError(error, "Could not sign in with Google"));
    }
  };

  return (
    <div className="mt-5 space-y-4">
      <div className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.12em] text-brand-subtle">
        <span className="h-px flex-1 bg-black/10" />
        or
        <span className="h-px flex-1 bg-black/10" />
      </div>
      <div className="flex justify-center [color-scheme:light]">
        <GoogleLogin
          onSuccess={handleSuccess}
          onError={() => toast.error("Could not sign in with Google")}
          text="continue_with"
          shape="rectangular"
          width="360"
        />
      </div>
    </div>
  );
};

const LoginView = ({ onLogin, onSendOtp, onVerifyOtp, onGoogle, setView, navigate, devPanel }) => {
  const [mode, setMode] = useState("otp");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [resendAfter, setResendAfter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [applicationPending, setApplicationPending] = useState(null);

  useEffect(() => {
    if (resendAfter <= 0) return undefined;
    const timer = window.setInterval(() => setResendAfter((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendAfter]);

  const showOtpError = (error) => {
    const response = error?.response;
    const detail = response?.data?.detail;
    if (response?.status === 503 && detail === "otp_unavailable") {
      setMode("password");
      toast.error("WhatsApp sign-in is unavailable right now, use your password");
      return;
    }
    if (response?.status === 429) {
      const wait = response.data?.retry_after || detail?.retry_after;
      toast.error(wait ? `Please wait ${wait} seconds before trying again` : "Please wait before trying again");
      return;
    }
    if (detail === "otp_locked" || detail?.code === "otp_locked") {
      setCodeSent(false);
      setOtp("");
      toast.error("Too many incorrect attempts. Please request a new code.");
      return;
    }
    const attemptsLeft = response?.data?.attempts_left || detail?.attempts_left;
    if (attemptsLeft != null) {
      toast.error(`Incorrect code. ${attemptsLeft} attempt${attemptsLeft === 1 ? "" : "s"} left.`);
      return;
    }
    toast.error(formatApiError(error, "Could not verify code"));
  };

  const sendCode = async () => {
    if (!identifier.trim()) return;
    setLoading(true);
    try {
      const data = await onSendOtp(identifier.trim());
      if (data?.hint === "use_phone") {
        setCodeSent(false);
        toast.error("Use your mobile number to receive a WhatsApp code");
        return;
      }
      setCodeSent(true);
      setResendAfter(data?.resend_after ?? 45);
      if (data?.dev_otp && process.env.NODE_ENV !== "production") setOtp(data.dev_otp);
    } catch (error) {
      showOtpError(error);
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await onLogin(identifier.trim(), password);
    } catch (error) {
      toast.error(formatApiError(error, "Could not sign in"));
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (event) => {
    event.preventDefault();
    if (!codeSent) {
      await sendCode();
      return;
    }
    setLoading(true);
    try {
      const data = await onVerifyOtp(identifier.trim(), otp);
      // A manual-tier applicant is not a new user: sending them to the profile
      // form would only fail on the application already under review.
      if (data?.status === "application_pending") {
        setApplicationPending({
          role: data.applying_as,
          submittedAt: data.submitted_at,
        });
        return;
      }
      if (data?.status === "new_user") {
        setView({
          name: "otp-apply",
          phone: data.phone,
          signupToken: data.signup_token,
          referredBy: data.referred_by || null,
        });
      }
    } catch (error) {
      showOtpError(error);
    } finally {
      setLoading(false);
    }
  };

  const dismissApplicationPending = () => {
    setApplicationPending(null);
    setCodeSent(false);
    setOtp("");
    setResendAfter(0);
  };

  if (applicationPending) {
    const submittedOn = formatSubmittedOn(applicationPending.submittedAt);
    return (
      <Shell
        title="Application under review"
        subtitle="Your Greentek Alliance registration is with our team"
        footer={<LinkButton onClick={dismissApplicationPending}>Back to sign in</LinkButton>}
      >
        <div
          className="flex items-start gap-3 rounded-[12px] bg-[#eef4ee] p-4"
          data-testid={LOGIN.applicationPending}
        >
          <CheckCircle2 className="mt-0.5 shrink-0 text-brand" size={20} />
          <div className="space-y-1">
            <p className="text-sm text-brand-ink">
              Your {roleLabel(applicationPending.role)} application is under review. We
              will email you when it is approved.
            </p>
            {submittedOn && (
              <p className="text-[12px] text-brand-muted">Submitted on {submittedOn}</p>
            )}
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      title="Welcome back"
      subtitle="Sign in to your Greentek Alliance account"
      footer={
        <>
          New to the Alliance?{" "}
          <LinkButton onClick={() => navigate("/register", { replace: true })} data-testid={LOGIN.registerLink}>Register now</LinkButton>
        </>
      }
    >
      <form onSubmit={mode === "otp" ? submitOtp : submitPassword} className="space-y-4">
        <Field
          id="identifier"
          label="Email or phone"
          type="text"
          autoComplete="username"
          required
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          placeholder="you@example.com or 10-digit mobile"
          data-testid={LOGIN.identifierInput}
        />
        {mode === "otp" ? (
          codeSent && (
            <Field
              id="otp-code"
              label="WhatsApp code"
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              data-testid={LOGIN.otpCodeInput}
            />
          )
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-[13px] font-medium text-brand-ink">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 rounded-[12px] border-black/10 bg-white"
              placeholder="Enter your password"
              data-testid={LOGIN.passwordInput}
            />
          </div>
        )}
        <SubmitButton
          loading={loading}
          data-testid={mode === "otp" ? (codeSent ? LOGIN.verifyOtpButton : LOGIN.sendOtpButton) : LOGIN.submitButton}
        >
          {mode === "otp" ? (codeSent ? "Verify" : "Send code") : "Sign in"}
        </SubmitButton>
        {mode === "otp" && codeSent && (
          <Button
            type="button"
            variant="ghost"
            disabled={loading || resendAfter > 0}
            onClick={sendCode}
            data-testid={LOGIN.resendOtpButton}
            className="w-full"
          >
            {resendAfter > 0 ? `Resend in ${resendAfter}s` : "Resend code"}
          </Button>
        )}
      </form>
      <p className="mt-4 text-center text-sm text-brand-muted">
        {mode === "otp" ? (
          <LinkButton onClick={() => setMode("password")} data-testid={LOGIN.passwordModeSwitchLink}>Sign in with password</LinkButton>
        ) : (
          <>
            Forgot password? Sign in with a{" "}
            <LinkButton onClick={() => setMode("otp")} data-testid={LOGIN.otpModeSwitchLink}>WhatsApp code</LinkButton>
            {" "}instead, or <LinkButton onClick={() => setView("forgot")} data-testid={LOGIN.forgotPasswordLink}>request an email link</LinkButton>.
          </>
        )}
      </p>
      <GoogleAuthButton onGoogle={onGoogle} />
      {devPanel}
    </Shell>
  );
};

const ApplyView = ({
  onApply,
  onSendOtp,
  onVerifyOtp,
  onGoogle,
  navigate,
  onBackToSignIn,
  verifiedPhone = "",
  signupToken: initialSignupToken = "",
  initialReferredBy = null,
}) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: verifiedPhone,
    state: "",
    city: "",
    existing_customer: "",
    referred_by_code: initialReferredBy?.code || "",
    message: "",
    accept_terms: false,
    signup_token: initialSignupToken,
  });
  const [otp, setOtp] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [resendAfter, setResendAfter] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [applicationPending, setApplicationPending] = useState(false);
  const [referredBy, setReferredBy] = useState(initialReferredBy);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const pick = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const signupToken = form.signup_token;
  const isVerified = Boolean(signupToken);
  const isRegistration = !initialSignupToken;

  useEffect(() => {
    if (resendAfter <= 0) return undefined;
    const timer = window.setInterval(() => setResendAfter((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendAfter]);

  const showOtpError = (error) => {
    const response = error?.response;
    const detail = response?.data?.detail;
    if (response?.status === 409 && detail === "phone_registered") {
      setAlreadyRegistered(true);
      setCodeSent(false);
      setOtp("");
      setResendAfter(0);
      return;
    }
    // Manual-tier applicants have no account yet, so the number is neither
    // registered nor free. Point them at sign-in rather than a second form.
    if (response?.status === 409 && detail === "application_pending") {
      setApplicationPending(true);
      setCodeSent(false);
      setOtp("");
      setResendAfter(0);
      return;
    }
    if (response?.status === 503 && detail === "otp_unavailable") {
      toast.error("WhatsApp registration is unavailable right now. Please try again later.");
      return;
    }
    if (response?.status === 429) {
      const wait = response.data?.retry_after || detail?.retry_after;
      toast.error(wait ? `Please wait ${wait} seconds before trying again` : "Please wait before trying again");
      return;
    }
    if (detail === "otp_locked" || detail?.code === "otp_locked") {
      setCodeSent(false);
      setOtp("");
      toast.error("Too many incorrect attempts. Please request a new code.");
      return;
    }
    const attemptsLeft = response?.data?.attempts_left ?? detail?.attempts_left;
    if (attemptsLeft != null) {
      toast.error(`Incorrect code. ${attemptsLeft} attempt${attemptsLeft === 1 ? "" : "s"} left.`);
      return;
    }
    toast.error(formatApiError(error, "Could not verify code"));
  };

  const changePhone = (event) => {
    const phone = event.target.value;
    setForm((prev) => ({ ...prev, phone }));
    setAlreadyRegistered(false);
    setApplicationPending(false);
    if (!isVerified) {
      setReferredBy(null);
      setCodeSent(false);
      setOtp("");
      setResendAfter(0);
    }
  };

  const sendCode = async () => {
    if (!isValidPhone(form.phone.trim())) {
      toast.error("Enter a valid mobile number");
      return;
    }
    setOtpLoading(true);
    setAlreadyRegistered(false);
    setApplicationPending(false);
    try {
      const data = await onSendOtp(form.phone.trim(), { purpose: "register" });
      setCodeSent(true);
      setResendAfter(data?.resend_after ?? 45);
      setOtp(data?.dev_otp && process.env.NODE_ENV !== "production" ? data.dev_otp : "");
    } catch (error) {
      showOtpError(error);
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyCode = async () => {
    setOtpLoading(true);
    setAlreadyRegistered(false);
    setApplicationPending(false);
    try {
      const data = await onVerifyOtp(form.phone.trim(), otp, { purpose: "register" });
      if (data?.status === "new_user") {
        const lockedReferral = data.referred_by || null;
        setReferredBy(lockedReferral);
        setForm((prev) => ({
          ...prev,
          phone: data.phone,
          signup_token: data.signup_token,
          referred_by_code: lockedReferral?.code || prev.referred_by_code,
        }));
        setCodeSent(false);
        setOtp("");
        setResendAfter(0);
      }
    } catch (error) {
      showOtpError(error);
    } finally {
      setOtpLoading(false);
    }
  };
  const backToSignIn = () => {
    if (onBackToSignIn) onBackToSignIn();
    else navigate("/", { replace: true });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.state) {
      toast.error("Please select your state");
      return;
    }
    if (!form.accept_terms) {
      toast.error("Please accept the Privacy Policy and Terms & Conditions");
      return;
    }
    setLoading(true);
    try {
      const res = await onApply({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        state: form.state,
        city: form.city.trim(),
        existing_customer: isRegistration ? form.existing_customer === "Yes" : null,
        referred_by_code: referredBy?.code || form.referred_by_code.trim() || null,
        message: isRegistration ? form.message.trim() || null : null,
        accept_terms: form.accept_terms,
        ...(signupToken ? { signup_token: signupToken } : {}),
      });
      // Registrations are Customer applications and are auto-approved, so the
      // applicant is logged straight in. Anything else falls back to review.
      if (res?.status !== "approved") setSubmitted(true);
    } catch (error) {
      if (error?.response?.data?.detail === "referral_code_unknown") {
        toast.error("That referral code was not found");
      } else if (error?.response?.data?.detail === "self_referral") {
        toast.error("You cannot use your own referral code");
      } else {
        toast.error(formatApiError(error, "Could not submit registration"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Shell
        title="Registration received"
        subtitle="Thanks for registering with Greentek Alliance"
        footer={<LinkButton onClick={backToSignIn}>Back to sign in</LinkButton>}
      >
        <div className="flex items-start gap-3 rounded-[12px] bg-[#eef4ee] p-4">
          <CheckCircle2 className="mt-0.5 shrink-0 text-brand" size={20} />
          <p className="text-sm text-brand-ink">
            Our team will review your registration and email you at{" "}
            <b>{form.email.trim()}</b>. Once approved, you'll get a link to set your
            password and open your dashboard.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      title={isRegistration ? "Register" : "Complete your profile"}
      subtitle="Tell us about you. Our team reviews every registration"
      footer={
        <>
          Already a member? <LinkButton onClick={backToSignIn}>Sign in</LinkButton>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field id="first-name" label="First name" autoComplete="given-name" required value={form.first_name} onChange={set("first_name")} placeholder="First" data-testid={REGISTER.firstNameInput} />
          <Field id="last-name" label="Last name" autoComplete="family-name" required value={form.last_name} onChange={set("last_name")} placeholder="Last" data-testid={REGISTER.lastNameInput} />
        </div>
        <Field id="apply-email" label="Email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid={REGISTER.emailInput} />
        <div className="space-y-1.5">
          <Label htmlFor="apply-phone" className="text-[13px] font-medium text-brand-ink">Phone number</Label>
          <div className="flex items-center gap-2">
            <Input
              id="apply-phone"
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
              required
              readOnly={isVerified || otpLoading}
              value={form.phone}
              onChange={changePhone}
              placeholder="10-digit mobile number"
              data-testid={REGISTER.phoneInput}
              className="h-11 min-w-0 flex-1 rounded-[12px] border-black/10 bg-white"
            />
            {isVerified ? (
              <span
                className="flex shrink-0 items-center gap-1 text-sm font-medium text-brand"
                data-testid={REGISTER.verifiedMarker}
              >
                <CheckCircle2 size={18} /> Verified
              </span>
            ) : (
              <Button
                type="button"
                variant="outline"
                disabled={otpLoading || codeSent}
                onClick={sendCode}
                data-testid={REGISTER.verifyButton}
                className="h-11 shrink-0 rounded-[12px]"
              >
                {otpLoading && !codeSent && <Loader2 className="mr-2 animate-spin" size={16} />}
                Verify
              </Button>
            )}
          </div>
          {alreadyRegistered && (
            <p
              className="text-sm text-red-700"
              data-testid={REGISTER.alreadyRegistered}
            >
              This number is already registered.{" "}
              <LinkButton onClick={backToSignIn}>Sign in</LinkButton>
            </p>
          )}
          {applicationPending && (
            <p
              className="text-sm text-red-700"
              data-testid={REGISTER.applicationPending}
            >
              This number has an application under review.{" "}
              <LinkButton onClick={backToSignIn}>Sign in</LinkButton>
            </p>
          )}
        </div>

        {codeSent && !isVerified && (
          <div className="space-y-2">
            <div className="flex items-end gap-2">
              <div className="min-w-0 flex-1 space-y-1.5">
                <Label htmlFor="register-code" className="text-[13px] font-medium text-brand-ink">WhatsApp code</Label>
                <Input
                  id="register-code"
                  type="text"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={otp}
                  onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit code"
                  data-testid={REGISTER.inlineCodeInput}
                  className="h-11 rounded-[12px] border-black/10 bg-white"
                />
              </div>
              <Button
                type="button"
                disabled={otpLoading || otp.length !== 6}
                onClick={verifyCode}
                data-testid={REGISTER.verifyCodeButton}
                className="h-11 shrink-0 rounded-[12px] bg-brand-forest text-white hover:bg-[#0d322d]"
              >
                {otpLoading && <Loader2 className="mr-2 animate-spin" size={16} />}
                Verify code
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              disabled={otpLoading || resendAfter > 0}
              onClick={sendCode}
              className="w-full"
            >
              {resendAfter > 0 ? `Resend in ${resendAfter}s` : "Resend code"}
            </Button>
          </div>
        )}

        {isRegistration && (
          <div className="space-y-1.5">
            <Label className="text-[13px] font-medium text-brand-ink">Existing Greentek customer?</Label>
            <Select value={form.existing_customer} onValueChange={pick("existing_customer")}>
              <SelectTrigger className="h-11 rounded-[12px] border-black/10 bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-[13px] font-medium text-brand-ink">State</Label>
            <Select value={form.state} onValueChange={pick("state")}>
              <SelectTrigger className="h-11 rounded-[12px] border-black/10 bg-white" data-testid={REGISTER.stateInput}><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {INDIAN_STATES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Field id="apply-city" label="City" required value={form.city} onChange={set("city")} placeholder="City" data-testid={REGISTER.cityInput} />
        </div>

        <div>
          <Field id="referred-by" label={referredBy ? "Referred by" : "Referred by (code, optional)"} value={form.referred_by_code} onChange={set("referred_by_code")} readOnly={Boolean(referredBy)} placeholder="GT-XXXX-000" data-testid={REGISTER.referredByInput} />
          {referredBy && (
            <p
              className="mt-1.5 text-[11px] text-brand-muted"
              data-testid={REGISTER.referredByLocked}
            >
              Referred by {referredBy.referrer_first_name}
            </p>
          )}
        </div>
        {isRegistration && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="apply-message" className="text-[13px] font-medium text-brand-ink">Message (optional)</Label>
              <textarea id="apply-message" value={form.message} onChange={set("message")} rows={3}
                className="w-full rounded-[12px] border border-black/10 bg-white p-3 text-sm" placeholder="Anything you'd like us to know" />
            </div>
          </>
        )}

        <div className="flex items-start gap-2.5 pt-1">
          <Checkbox
            id="accept-terms"
            checked={form.accept_terms}
            onCheckedChange={(v) => setForm((p) => ({ ...p, accept_terms: v === true }))}
            className="mt-0.5"
            data-testid={REGISTER.termsCheckbox}
          />
          <Label htmlFor="accept-terms" className="text-[12.5px] font-normal leading-snug text-brand-muted">
            I have read and agree to the{" "}
            <Link to="/legal/privacy" onClick={(e) => e.stopPropagation()} className="font-medium text-brand-gold-deep underline">Privacy Policy</Link>
            {" "}and{" "}
            <Link to="/legal/terms" onClick={(e) => e.stopPropagation()} className="font-medium text-brand-gold-deep underline">Terms &amp; Conditions</Link>, and I consent to Greentek processing my personal data for the purposes described in the Privacy Policy.
          </Label>
        </div>

        <SubmitButton loading={loading} disabled={loading || !isVerified} data-testid={REGISTER.submitButton}>Submit registration</SubmitButton>
      </form>
      {isRegistration && <GoogleAuthButton onGoogle={onGoogle} />}
    </Shell>
  );
};

const ForgotView = ({ setView }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = await forgotPasswordRequest(email.trim());
      setSent(true);
      setDevLink(data?.dev_reset_link || null);
    } catch (error) {
      toast.error(formatApiError(error, "Could not send reset link"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell
      title="Reset your password"
      subtitle="We'll email you a link to set a new password"
      footer={<LinkButton onClick={() => setView("login")}>Back to sign in</LinkButton>}
    >
      {sent ? (
        <div className="space-y-4">
          <p className="rounded-[12px] bg-[#eef4ee] p-4 text-sm text-brand-ink">
            If that email is registered, a reset link is on its way. The link is valid for 1 hour.
          </p>
          {devLink && (
            <div className="rounded-[12px] border border-dashed border-brand-gold-metal bg-[#fbf6e9] p-3 text-xs">
              <p className="mb-1 font-semibold text-brand-gold-deep">Dev mode: email not configured:</p>
              <a className="break-all text-brand-gold-deep underline" href={devLink}>
                {devLink}
              </a>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field
            id="forgot-email"
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
          <SubmitButton loading={loading}>Send reset link</SubmitButton>
        </form>
      )}
    </Shell>
  );
};

const ResetView = ({ token, setView, clearToken }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await resetPasswordRequest(token, password);
      toast.success("Password updated. Please sign in.");
      clearToken();
      setView("login");
    } catch (error) {
      toast.error(formatApiError(error, "Could not reset password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Set a new password" subtitle="Choose a new password for your account">
      <form onSubmit={submit} className="space-y-4">
        <Field
          id="new-password"
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          hint="Minimum 8 characters."
        />
        <Field
          id="confirm-password"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Re-enter password"
        />
        <SubmitButton loading={loading}>Update password</SubmitButton>
      </form>
    </Shell>
  );
};

const DevPanel = ({ onStartDevRole }) => {
  const [role, setRole] = useState("customer");
  const [busy, setBusy] = useState(false);

  const enter = async () => {
    setBusy(true);
    try {
      await onStartDevRole(role);
    } catch {
      /* toast already shown by the hook */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-[14px] border border-dashed border-black/15 bg-black/[.02] p-4">
      <p className="text-[11px] font-bold uppercase tracking-[.14em] text-brand-subtle">
        Developer preview
      </p>
      <p className="mt-1 text-xs text-brand-muted">
        Skip login and open the app as any role. Not available in production.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-10 flex-1 rounded-[10px] border-black/10 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DEV_ROLES.map((r) => (
              <SelectItem key={r} value={r} data-testid={`role-${r}`}>
                {roleLabel(r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={enter}
          className="h-10 rounded-[10px]"
        >
          Enter
        </Button>
      </div>
    </div>
  );
};

export const AuthScreen = ({ initialView = "login", resetToken, onLogin, onSendOtp, onVerifyOtp, onGoogle, onApply, onStartDevRole }) => {
  const [view, setView] = useState(resetToken ? "reset" : initialView);
  const navigate = useNavigate();

  // login / register / reset are URL-driven (App.js derives initialView from the
  // path). Keep the local view in sync when that URL-derived view changes,
  // without remounting because a remount would wipe a half-filled registration form.
  // forgot/reset in-screen toggles that aren't URL-backed stay as plain setView.
  useEffect(() => {
    setView(resetToken ? "reset" : initialView);
  }, [initialView, resetToken]);

  const clearToken = () => {
    if (typeof window !== "undefined" && window.history?.replaceState) {
      window.history.replaceState({}, "", "/");
    }
  };

  const devPanel = DEV_TOOLS_ENABLED ? <DevPanel onStartDevRole={onStartDevRole} /> : null;

  if (view === "reset" && resetToken) {
    return <ResetView token={resetToken} setView={setView} clearToken={clearToken} />;
  }
  if (view === "apply") {
    return <ApplyView onApply={onApply} onSendOtp={onSendOtp} onVerifyOtp={onVerifyOtp} onGoogle={onGoogle} navigate={navigate} />;
  }
  if (view?.name === "otp-apply") {
    return <ApplyView onApply={onApply} onSendOtp={onSendOtp} onVerifyOtp={onVerifyOtp} onGoogle={onGoogle} navigate={navigate} onBackToSignIn={() => setView("login")} verifiedPhone={view.phone} signupToken={view.signupToken} initialReferredBy={view.referredBy} />;
  }
  if (view === "forgot") return <ForgotView setView={setView} />;
  return <LoginView onLogin={onLogin} onSendOtp={onSendOtp} onVerifyOtp={onVerifyOtp} onGoogle={onGoogle} setView={setView} navigate={navigate} devPanel={devPanel} />;
};
